// ─────────────────────────────────────────────────────────────────────────────
// NotificationsBell, CD's .iconbtn bell + the anchored notifications drawer.
// 1:1 port of site/js/notifications-wiring.js (drawer DOM, row rendering, mark/
// dismiss/clear, roster-invite Accept/Decline, outside-click + Esc close, the
// 30s poll loop). Data + actions come from lib/useNotifications. CD's red dot
// (.iconbtn__dot) shows only when there are unread items. CSS in notifications.css.
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  useNotifications,
  timeAgo,
  stripBandPrefix,
  kindToLabel,
  inviteMembershipId,
  resolveNotifDest,
  type AcNotification,
} from '../../lib/notifications';
import { BellIcon } from './glyphs';
import './notifications.css';

/** One drawer row, kind label, title, body, time, optional invite actions. */
function NotifRow({
  n,
  onRowClick,
  onDismiss,
  onInvite,
}: {
  readonly n: AcNotification;
  readonly onRowClick: (n: AcNotification) => void;
  readonly onDismiss: (id: string | number) => void;
  readonly onInvite: (n: AcNotification, action: 'accept' | 'decline') => void;
}): JSX.Element {
  const unread = !n.read;
  const title = stripBandPrefix(n.title);
  const membershipId = n.kind === 'roster_invite' ? inviteMembershipId(n) : null;
  const kindClass = 'notif-row--kind-' + String(n.kind || '').replace(/_/g, '-');

  return (
    <div
      className={'notif-row ' + kindClass + (unread ? ' notif-row--unread' : '')}
      onClick={() => onRowClick(n)}
    >
      <button
        type="button"
        className="notif-row__dismiss"
        aria-label="Dismiss notification"
        title="Dismiss"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onDismiss(n.id);
        }}
      >
        ×
      </button>
      <div className="notif-row__kind">{kindToLabel(n.kind)}</div>
      <div className="notif-row__title">{title}</div>
      {n.body ? <div className="notif-row__body">{n.body}</div> : null}
      <div className="notif-row__time">{timeAgo(n.created_at)}</div>
      {membershipId ? (
        <div className="notif-row__actions">
          <button
            type="button"
            className="notif-row__btn notif-row__btn--accept"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onInvite(n, 'accept');
            }}
          >
            Accept
          </button>
          <button
            type="button"
            className="notif-row__btn notif-row__btn--decline"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onInvite(n, 'decline');
            }}
          >
            Decline
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function NotificationsBell(): JSX.Element {
  const notif = useNotifications();
  const { items, unread, open, setOpen, markRead, markAll, dismiss, clearAll, respondInvite } =
    notif;
  const bellRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties>({});

  // Anchor the drawer under the bell (positionDrawer() :230).
  const reposition = useCallback(() => {
    const btn = bellRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Outside-click + Esc close while open (open()/close() :465-486).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: globalThis.MouseEvent): void => {
      const t = e.target as Node;
      if (bellRef.current?.contains(t) || drawerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onResize = (): void => reposition();
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onEsc, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onEsc, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, setOpen, reposition]);

  /* notif-routing 2026-07-19 (Ryan), the old handler set window.location
     .hash to a legacy mobile path the desktop app has never routed on, so
     every bell click was a dead end. Resolve to a real destination: thread/
     inbox routes open the Messages overlay (TopNav listens for
     ac:open-messages), everything else navigates the react-router path. */
  const navigate = useNavigate();
  const onRowClick = useCallback(
    (n: AcNotification) => {
      if (!n.read) markRead(n.id);
      const dest = resolveNotifDest(n.route);
      if (!dest) return;
      setOpen(false);
      if (dest.type === 'thread') {
        window.dispatchEvent(new CustomEvent('ac:open-messages', { detail: { threadId: dest.threadId } }));
      } else if (dest.type === 'inbox') {
        window.dispatchEvent(new CustomEvent('ac:open-messages', { detail: {} }));
      } else {
        navigate(dest.path);
      }
    },
    [markRead, setOpen, navigate],
  );

  return (
    <>
      <button
        ref={bellRef}
        className="iconbtn"
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          setOpen(!open);
        }}
      >
        {unread > 0 ? <span className="iconbtn__dot"></span> : null}
        <BellIcon />
      </button>

      {open ? (
        <div
          ref={drawerRef}
          className="notif-drawer"
          role="dialog"
          aria-label="Notifications"
          style={pos}
        >
          <div className="notif-drawer__head">
            <span className="notif-drawer__title">Notifications</span>
            <button
              type="button"
              className="notif-drawer__mark-all"
              onClick={(e) => {
                e.preventDefault();
                markAll();
              }}
            >
              Mark all read
            </button>
          </div>
          <div className="notif-drawer__list">
            {items.length === 0 ? (
              <div className="notif-empty">
                <p>No notifications yet.</p>
                <p className="notif-empty__sub">
                  You'll see alerts here when a post is taking off, when you get a show invite, or
                  when one of your songs lands somewhere new.
                </p>
              </div>
            ) : (
              items.map((n) => (
                <NotifRow
                  key={n.id}
                  n={n}
                  onRowClick={onRowClick}
                  onDismiss={dismiss}
                  onInvite={respondInvite}
                />
              ))
            )}
          </div>
          {items.length > 0 ? (
            <div className="notif-drawer__foot">
              <button
                type="button"
                className="notif-drawer__clear-all"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
              >
                Clear all
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
